import React, { useEffect, useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { playerService } from '../../../../services/player';
import { uiActions } from '../../../../store/slices/ui';
import { spotifyActions } from '../../../../store/slices/spotify';
import { msToTime } from '../../../../utils';
import { getImageAnalysis2 } from '../../../../utils/imageAnyliser';
import tinycolor from 'tinycolor2';
import { Slider } from '../../../Slider';
import { AddSongToLibraryButton } from '../../../Actions/AddSongToLibrary';
import {
  ShuffleIcon,
  SkipBack,
  SkipNext,
  Replay,
  ReplayOne,
  Play,
  Pause,
  ListIcon,
} from '../../../Icons';
import { MobileLyricsCard } from './MobileLyricsCard';
import { MobileAboutArtistCard } from './MobileAboutArtistCard';
import { MobileCreditsCard } from './MobileCreditsCard';
import './MobileNowPlayingModal.scss';

interface MobileNowPlayingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MobileNowPlayingModal: React.FC<MobileNowPlayingModalProps> = memo(({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentSong = useAppSelector((state) => state.spotify.state?.track_window.current_track);
  const paused = useAppSelector((state) => state.spotify.state?.paused ?? true);
  const position = useAppSelector((state) => state.spotify.state?.position || 0);
  const duration = useAppSelector((state) => state.spotify.state?.duration || 1);
  const shuffle = useAppSelector((state) => state.spotify.state?.shuffle ?? false);
  const repeatMode = useAppSelector((state) => state.spotify.state?.repeat_mode ?? 0);
  const liked = useAppSelector((state) => state.spotify.liked);

  const [currentColor, setColor] = useState('#283038');
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [isSeeking, setIsSeeking] = useState<boolean>(false);
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);

  // Extract color from album artwork
  useEffect(() => {
    if (currentSong?.album?.images?.[0]?.url) {
      getImageAnalysis2(currentSong.album.images[0].url).then((dominant) => {
        let color = tinycolor(dominant);
        while (color.isLight()) {
          color = color.darken(8);
        }
        setColor(color.toHexString());
      });
    }
  }, [currentSong]);

  // Sync seekbar with song position unless user is actively dragging
  useEffect(() => {
    if (!isSeeking && duration > 0) {
      setSliderValue(position / duration);
    }
  }, [position, duration, isSeeking]);

  if (!currentSong) return null;

  const handleSeekEnd = (val: number | null | undefined) => {
    setIsSeeking(false);
    if (val === null || val === undefined || isNaN(val)) return;
    setSliderValue(val);
    const newPos = Math.round(val * duration);
    playerService.seekToPosition(newPos).then();
  };

  const toggleShuffle = () => {
    playerService.toggleShuffle(!shuffle).then();
  };

  const toggleRepeat = () => {
    const nextMode = repeatMode === 0 ? 'context' : repeatMode === 1 ? 'track' : 'off';
    playerService.setRepeatMode(nextMode).then();
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: currentSong.name,
        text: `Listening to ${currentSong.name} by ${currentSong.artists?.map((a: any) => a.name).join(', ')} on OpenMusic`,
        url: window.location.origin + `/track/${currentSong.id}`,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.origin + `/track/${currentSong.id}`);
    }
  };

  const artistsList = currentSong.artists?.map((a: any) => a.name).join(', ') || '';
  const coverUrl = currentSong.album?.images?.[0]?.url || '';
  const firstArtist = currentSong.artists?.[0] as any;
  const artistId = firstArtist?.id || firstArtist?.uri?.split(':').reverse()[0] || '';
  const mainArtistName = firstArtist?.name || 'Artist';

  return (
    <>
      <div
        className={`mobile-now-playing-modal ${isOpen ? 'open' : ''}`}
        style={{
          background: `linear-gradient(180deg, ${currentColor} 0%, #121212 90%)`,
        }}
      >
        {/* Top Header Bar */}
        <div className='mnp-header'>
          <button
            className='mnp-icon-btn mnp-collapse-btn'
            onClick={onClose}
            aria-label='Collapse Now Playing'
          >
            <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='currentColor'>
              <path d='M2.97 5.47a.75.75 0 0 1 1.06 0L12 13.44l7.97-7.97a.75.75 0 1 1 1.06 1.06l-8.5 8.5a.75.75 0 0 1-1.06 0l-8.5-8.5a.75.75 0 0 1 0-1.06z' />
            </svg>
          </button>

          <div className='mnp-header-title-container'>
            <span className='mnp-header-context-title'>{currentSong.name}</span>
          </div>

          <button
            className='mnp-icon-btn mnp-options-btn'
            aria-label='More options'
            onClick={() => setIsOptionsMenuOpen(true)}
          >
            <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='currentColor'>
              <path d='M4.5 10.5C3.67 10.5 3 11.17 3 12s.67 1.5 1.5 1.5S6 12.83 6 12s-.67-1.5-1.5-1.5zm7.5 0c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm7.5 0c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z' />
            </svg>
          </button>
        </div>

        {/* Scrollable Content Container (Screen 1 + Screen 2 Cards) */}
        <div className='mnp-scroll-content'>
          {/* Main Player Screen View */}
          <div className='mnp-main-player-view'>
            {/* Center Artwork */}
            <div className='mnp-artwork-container'>
              <img
                src={coverUrl}
                alt={currentSong.name}
                className='mnp-artwork-img'
              />
            </div>

            {/* Song Details Row */}
            <div className='mnp-details-row'>
              <div className='mnp-details-text'>
                <div className='mnp-song-title'>{currentSong.name}</div>
                <div className='mnp-artist-name'>{artistsList}</div>
              </div>

              <div className='mnp-like-button-container'>
                <AddSongToLibraryButton
                  size={26}
                  isSaved={liked}
                  id={currentSong.id || ''}
                  onToggle={() => dispatch(spotifyActions.setLiked({ liked: !liked }))}
                />
              </div>
            </div>

            {/* Seekbar and Timers */}
            <div className='mnp-seekbar-container'>
              <Slider
                isEnabled={true}
                value={sliderValue}
                onChange={(val) => {
                  setIsSeeking(true);
                  setSliderValue(val);
                }}
                onChangeEnd={handleSeekEnd}
              />
              <div className='mnp-time-row'>
                <span>{msToTime(sliderValue * duration)}</span>
                <span>{msToTime(duration)}</span>
              </div>
            </div>

            {/* Playback Controls (Shuffle, Prev, Play/Pause, Next, Repeat) */}
            <div className='mnp-controls-row'>
              <button
                className={`mnp-ctrl-btn ${shuffle ? 'active' : ''}`}
                onClick={toggleShuffle}
                aria-label='Shuffle'
              >
                <ShuffleIcon />
              </button>

              <button
                className='mnp-ctrl-btn'
                onClick={() => playerService.previousTrack().then()}
                aria-label='Previous track'
              >
                <SkipBack />
              </button>

              <button
                className='mnp-play-circle-btn'
                onClick={() => (!paused ? playerService.pausePlayback() : playerService.startPlayback())}
                aria-label={paused ? 'Play' : 'Pause'}
              >
                {paused ? <Play /> : <Pause />}
              </button>

              <button
                className='mnp-ctrl-btn'
                onClick={() => playerService.nextTrack().then()}
                aria-label='Next track'
              >
                <SkipNext />
              </button>

              <button
                className={`mnp-ctrl-btn ${repeatMode !== 0 ? 'active' : ''}`}
                onClick={toggleRepeat}
                aria-label='Repeat'
              >
                {repeatMode === 2 ? (
                  <ReplayOne active={true} />
                ) : (
                  <Replay active={repeatMode === 1} />
                )}
              </button>
            </div>

            {/* Bottom Action Row (Device connection on left, Share & Queue on right) */}
            <div className='mnp-bottom-row'>
              <div className='mnp-device-label'>
                <svg role='img' height='14' width='14' viewBox='0 0 24 24' fill='#1db954'>
                  <path d='M17 1H7c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm0 18H7V5h10v14z' />
                </svg>
                <span>Wired connection</span>
              </div>

              <div className='mnp-bottom-right-btns'>
                <button
                  className='mnp-icon-btn'
                  onClick={handleShare}
                  aria-label='Share'
                >
                  <svg role='img' height='20' width='20' viewBox='0 0 24 24' fill='currentColor'>
                    <path d='M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z' />
                  </svg>
                </button>

                <button
                  className='mnp-icon-btn'
                  onClick={() => dispatch(uiActions.toggleQueue())}
                  aria-label='Queue'
                >
                  <ListIcon />
                </button>
              </div>
            </div>
          </div>

          {/* Section 2: Scrolled Down Cards (Lyrics, About Artist, Credits) */}
          <div className='mnp-scroll-cards-section'>
            <MobileLyricsCard
              song={currentSong}
              position={position}
              duration={duration}
              paused={paused}
              dominantColor={currentColor}
            />

            {artistId && (
              <MobileAboutArtistCard
                artistId={artistId}
                artistName={mainArtistName}
                onCloseParent={onClose}
              />
            )}

            <MobileCreditsCard
              song={currentSong}
              onCloseParent={onClose}
            />
          </div>
        </div>
      </div>

      {/* Three Dots More Options Menu Overlay */}
      <div
        className={`mnp-options-overlay ${isOptionsMenuOpen ? 'open' : ''}`}
        onClick={() => setIsOptionsMenuOpen(false)}
      >
        <div
          className='mnp-options-card'
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top Track Header */}
          <div className='mnp-options-track-header'>
            <img
              src={coverUrl}
              alt={currentSong.name}
              className='mnp-options-track-img'
            />
            <div className='mnp-options-track-meta'>
              <h3 className='mnp-options-track-title'>{currentSong.name}</h3>
              <p className='mnp-options-track-artist'>{artistsList}</p>
            </div>
          </div>

          {/* Action List */}
          <div className='mnp-options-list'>
            {/* 1. Like */}
            <div
              className='mnp-options-item'
              onClick={() => {
                dispatch(spotifyActions.setLiked({ liked: !liked }));
              }}
            >
              <div className='mnp-options-icon'>
                {liked ? (
                  <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='#1db954'>
                    <path d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z' />
                  </svg>
                ) : (
                  <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='none' stroke='#ffffff' strokeWidth='2'>
                    <path d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z' />
                  </svg>
                )}
              </div>
              <span className='mnp-options-label'>{liked ? 'Liked' : 'Like'}</span>
            </div>

            {/* 2. Share */}
            <div
              className='mnp-options-item'
              onClick={() => {
                handleShare();
                setIsOptionsMenuOpen(false);
              }}
            >
              <div className='mnp-options-icon'>
                <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='#ffffff'>
                  <path d='M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z' />
                </svg>
              </div>
              <span className='mnp-options-label'>Share</span>
            </div>

            {/* 3. View artist */}
            <div
              className='mnp-options-item'
              onClick={() => {
                if (artistId) {
                  navigate(`/artist/${artistId}`);
                  setIsOptionsMenuOpen(false);
                  onClose();
                }
              }}
            >
              <div className='mnp-options-icon'>
                <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='#ffffff'>
                  <path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' />
                </svg>
              </div>
              <span className='mnp-options-label'>View artist</span>
            </div>

            {/* 4. View album */}
            <div
              className='mnp-options-item'
              onClick={() => {
                const album = currentSong.album as any;
                const albumId = album?.id || album?.uri?.split(':').reverse()[0];
                if (albumId) {
                  navigate(`/album/${albumId}`);
                  setIsOptionsMenuOpen(false);
                  onClose();
                }
              }}
            >
              <div className='mnp-options-icon'>
                <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='#ffffff'>
                  <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 14.5c-2.49 0-4.5-2.01-4.5-4.5S9.51 7.5 12 7.5s4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5zm0-5.5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z' />
                </svg>
              </div>
              <span className='mnp-options-label'>View album</span>
            </div>
          </div>

          {/* Bottom Close Button */}
          <div className='mnp-options-close-row'>
            <button
              className='mnp-options-close-btn'
              onClick={() => setIsOptionsMenuOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
});

MobileNowPlayingModal.displayName = 'MobileNowPlayingModal';
export default MobileNowPlayingModal;
